/**
 * Public (un-authed) API for the private proposal share page (v2-1-4).
 *
 * The client opens an unguessable link /p/<token> with NO account; this fetches
 * the client-facing proposal view via plain fetch (no Authorization header). A
 * 404 is the friendly "link not found" state; any other failure is a generic
 * error. NOTHING about the admin session is involved here.
 */

export type PublicMediaType = 'pdf' | 'video';

export interface PublicProposalItem {
  label: string;
  url: string;
  media_type: PublicMediaType;
}

export interface PublicProposal {
  client_name: string;
  campaign_title: string;
  intro_md: string | null;
  items: PublicProposalItem[];
}

export class ProposalNotFoundError extends Error {
  constructor() {
    super('Proposal not found');
    this.name = 'ProposalNotFoundError';
  }
}

/** Fetch a proposal by its private token. Throws ProposalNotFoundError on 404. */
export async function fetchPublicProposal(token: string): Promise<PublicProposal> {
  let resp: Response;
  try {
    resp = await fetch(`/api/v1/proposals/p/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new Error('We could not reach the server. Please try again.');
  }
  if (resp.status === 404) throw new ProposalNotFoundError();
  if (!resp.ok) throw new Error('Something went wrong loading this proposal.');
  return (await resp.json()) as PublicProposal;
}
